import { useEffect, useState } from "react";
import type { Capture } from "../../shared/types";
import { api, imageUrl } from "../api";
import { IconDownload } from "../icons";

const host = (u: string) => { try { return new URL(u).host; } catch { return u; } };
const size = (b: number) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

/** Capture library plus setup for the browser extension. */
export default function CapturesPage() {
  const [caps, setCaps] = useState<Capture[] | null>(null);
  const [setup, setSetup] = useState<{ token: string; serverUrl: string; captureOrigin: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => api.captures().then(setCaps).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    api.captureSetup().then(setSetup).catch(() => {});
    // New captures arrive from the extension, so keep the list fresh while the page is open.
    const t = setInterval(() => { if (document.visibilityState === "visible") api.captures().then(setCaps).catch(() => {}); }, 5000);
    return () => clearInterval(t);
  }, []);

  const copy = async (v: string) => { await navigator.clipboard.writeText(v); setCopied(v); setTimeout(() => setCopied(null), 1500); };
  const rename = async (c: Capture) => {
    const title = prompt("Capture name", c.title)?.trim();
    if (title && title !== c.title) { await api.renameCapture(c.id, title); load(); }
  };
  const remove = async (c: Capture) => {
    if (!confirm(`Remove "${c.title}" from the library? Screens already made from it keep working.`)) return;
    await api.deleteCapture(c.id);
    load();
  };

  return (
    <div className="stack stack-l" style={{ maxWidth: 1100 }}>
      <div className="page-head">
        <div><div className="eyebrow">Library</div><h1>Captures</h1>
          <p className="sub">Pages recorded as live HTML with the DemoDive Capture extension. Add them to a demo from its Studio.</p></div>
      </div>

      <section className="panel stack">
        <h2>Set up the capture extension (Chrome or Edge)</h2>
        <ol className="setup-steps">
          <li><a className="btn btn-sm" href="/downloads/demodive-capture.zip" download><IconDownload />Download the extension</a> and unzip it.</li>
          <li>Open <code>chrome://extensions</code> (or <code>edge://extensions</code>), turn on <b>Developer mode</b>, click <b>Load unpacked</b> and pick the <code>demodive-capture</code> folder. Pin it from the puzzle-piece menu.</li>
          <li><b>Copy connection code</b> below, click the extension's icon and paste it into <b>Connection code</b>. It saves and tests itself.</li>
          <li>Open the page you want (e.g. your demo tenant), get it looking right, then click <b>Capture this page</b>. It appears in the list below.</li>
        </ol>
        {setup && (
          <div className="row">
            <button className="btn btn-primary" onClick={() => copy(`${setup.serverUrl}#${setup.token}`)}>
              {copied === `${setup.serverUrl}#${setup.token}` ? "Copied" : "Copy connection code"}
            </button>
            <span className="small muted">Server <span className="mono">{setup.serverUrl}</span> plus your capture token. Treat it like a password; changing the server's SESSION_SECRET revokes it.</span>
          </div>
        )}
        {setup && !setup.captureOrigin && (
          <div className="route-note">No capture domain is set (<code>CAPTURE_ORIGIN</code>), so captures replay from this domain inside a strict sandbox. Point a subdomain like <code>capture.ironhullsystems.com</code> at this site and set <code>CAPTURE_ORIGIN</code> to isolate them fully.</div>
        )}
      </section>

      {error && <div className="error-box">{error}</div>}
      {!caps ? <p className="muted">Loading…</p> : caps.length === 0 ? (
        <div className="panel"><p className="muted">No captures yet. Capture a page with the extension and it appears here.</p></div>
      ) : (
        <div className="cap-grid">
          {caps.map((c) => (
            <div key={c.id} className="cap-card">
              <div className="cap-shot" style={{ backgroundImage: `url(${imageUrl(c.screenshotKey)})` }} />
              <div className="cap-body">
                <b title={c.title}>{c.title}</b>
                <span className="mono small muted" title={c.sourceUrl}>{host(c.sourceUrl)}</span>
                <span className="small muted">{new Date(c.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} · {c.width}×{c.height} · {size(c.bytes)}</span>
                <div className="thumb-actions"><button className="btn btn-sm" onClick={() => rename(c)}>Rename</button><button className="btn btn-sm btn-danger" onClick={() => remove(c)}>Remove</button></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Modal picker used in the Studio. */
export function CapturePicker({ onPick, onClose }: { onPick: (c: Capture) => void; onClose: () => void }) {
  const [caps, setCaps] = useState<Capture[] | null>(null);
  useEffect(() => { api.captures().then(setCaps).catch(() => setCaps([])); }, []);
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Add HTML capture" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-card narrow">
        <div className="row"><h2>Add an HTML capture</h2><span className="spacer" /><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
        {!caps ? <p className="muted">Loading…</p> : caps.length === 0 ? (
          <p className="muted">No captures yet. Set up the extension on the <a href="/admin/captures">Captures</a> page, capture a page, then come back.</p>
        ) : (
          <div className="cap-grid">
            {caps.map((c) => (
              <button key={c.id} type="button" className="cap-card" onClick={() => onPick(c)}>
                <div className="cap-shot" style={{ backgroundImage: `url(${imageUrl(c.screenshotKey)})` }} />
                <div className="cap-body"><b>{c.title}</b><span className="mono small muted">{host(c.sourceUrl)}</span></div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
